import React, { useMemo, useRef } from 'react'
import classNames from 'classnames'
import { MessageWrapper } from './MessageWrapper'
import { MessageSwitchProps } from '../MessageSwitch'
import emojiToImg from '~/utils/emojiToImg'
import { useNarrow } from '~/utils/useNarrow'
import { MessageClient } from '~/service/chatConn/types'
import { useEmojiOnly } from '~/utils/hooks'

type QuestionProps = MessageSwitchProps<MessageClient>

/**
 * Possible values of text in user Question:
 *  - plain text
 *  - HTML code with link to an attachment
 *  - HTML sent by user that is then encoded by SAAS
 *  - HTML encoded characters with accents
 *
 * We need to let it go through `dangerouslySetInnerHTML`, so the encoded
 * accents and emojis will work.
 * We cannot let it go through he.decode(), because it will decode all HTML code
 * written by user.
 * We cannot use he.decode() and then remove the HTML, because it will remove
 * HTML generated by SAAS
 */
const Question: React.FC<QuestionProps> = ({ ...props }) => {
  const { message } = props
  const emojiOnly = useEmojiOnly(message.content)

  const text = useMemo(() => emojiToImg(message.content), [])

  const ref = useRef<HTMLDivElement>(null)

  if (!message.form) {
    useNarrow(ref, props.parentWidth)
  }

  return (
    <MessageWrapper
      className={classNames('is-question', { 'is-emojiOnly': emojiOnly })}
      _ref={ref}
      {...props}
    >
      {message.form ? (
        <table className="table">
          <tbody>
            {message.form.map((row, i) => (
              <tr key={i}>
                <th>{row.name}</th>
                <td>{row.value}</td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <span dangerouslySetInnerHTML={{ __html: text }} />
      )}

      <svg aria-hidden="true">
        <rect x="0" />
      </svg>
    </MessageWrapper>
  )
}

export default Question
